import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, User, Loader2, Play } from "lucide-react";

const CATEGORIES = [
  { id: "artist",   label: "Artist",              emoji: "🎨" },
  { id: "actor",    label: "Actor",               emoji: "🎬" },
  { id: "actress",  label: "Actress",             emoji: "🌟" },
  { id: "model",    label: "Model",               emoji: "💃" },
  { id: "sports",   label: "Sports Personalities",emoji: "🏆" },
  { id: "chef",     label: "Chef",                emoji: "👨‍🍳" },
];

export default function BookArtist() {
  const nav = useNavigate();
  const [category, setCategory] = useState("");
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!category) { setArtists([]); return; }
    setLoading(true);
    api.get(`/artists?category=${encodeURIComponent(category)}`)
      .then(r => setArtists(r.data || []))
      .finally(() => setLoading(false));
  }, [category]);

  return (
    <div className="px-4 py-5 pb-24" data-testid="book-artist-page">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => nav(-1)} data-testid="book-artist-back" className="p-2 rounded-full bg-white/8"><ArrowLeft className="w-4 h-4"/></button>
        <div>
          <h1 className="font-outfit font-extrabold text-2xl leading-tight flex items-center gap-2">
            <User className="w-6 h-6 text-[#FF7A00]"/>Book Your Artist
          </h1>
          <p className="text-white/60 text-xs">Pick a category to browse profiles</p>
        </div>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-wider text-white/60 font-bold mb-1 block">Category</label>
        <select value={category} onChange={e => setCategory(e.target.value)} data-testid="book-artist-category"
          className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/15 outline-none focus:border-[#FF7A00] appearance-none text-base">
          <option value="" className="bg-[#0B0F19]">Select a category…</option>
          {CATEGORIES.map(c => <option key={c.id} value={c.id} className="bg-[#0B0F19]">{c.emoji} {c.label}</option>)}
        </select>
      </div>

      {category && (
        <div className="mt-5">
          {loading ? (
            <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#FF7A00] mx-auto"/></div>
          ) : artists.length === 0 ? (
            <div className="text-center py-12 text-white/55 text-sm">No profiles yet in this category.</div>
          ) : (
            <div className="grid grid-cols-2 gap-3" data-testid="artist-list">
              {artists.map(a => (
                <button key={a.id} onClick={() => setSelected(a)} data-testid={`artist-card-${a.id}`}
                  className="text-left rounded-2xl overflow-hidden border border-white/10 bg-white/5 active:scale-95 transition">
                  <div className="aspect-[3/4] bg-gradient-to-br from-[#FF7A00]/20 to-[#A855F7]/20 overflow-hidden">
                    {a.photos?.[0] ? <img src={a.photos[0]} alt="" className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center"><User className="w-10 h-10 text-white/30"/></div>}
                  </div>
                  <div className="p-2.5">
                    <div className="font-bold text-sm truncate">{a.first_name} {a.last_name}</div>
                    <div className="text-[10px] text-white/60">{CATEGORIES.find(c => c.id === a.category)?.label} · {a.age ? `${a.age} yrs` : ""}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-[80] bg-black/80 flex items-end sm:items-center justify-center p-3" onClick={() => setSelected(null)}>
          <div onClick={e => e.stopPropagation()} data-testid="artist-detail-modal"
            className="w-full sm:max-w-lg max-h-[85vh] overflow-y-auto bg-[#0B0F19] border border-white/15 rounded-3xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="font-outfit font-extrabold text-xl">{selected.first_name} {selected.last_name}</h2>
                <div className="text-xs text-white/60">{CATEGORIES.find(c => c.id === selected.category)?.label}{selected.age ? ` · ${selected.age} yrs` : ""}</div>
              </div>
              <button onClick={() => setSelected(null)} className="p-1.5 rounded-full bg-white/8" data-testid="artist-detail-close">✕</button>
            </div>
            {(selected.photos || []).length > 0 && (
              <div className="grid grid-cols-3 gap-1.5 mb-3">
                {selected.photos.map((p, i) => <img key={i} src={p} alt="" className="aspect-square object-cover rounded-lg"/>)}
              </div>
            )}
            {(selected.videos || []).length > 0 && (
              <div className="space-y-2 mb-3">
                {selected.videos.map((v, i) => <video key={i} src={v} controls className="w-full rounded-lg"/>)}
              </div>
            )}
            {selected.description && <p className="text-sm text-white/80 whitespace-pre-wrap leading-relaxed">{selected.description}</p>}
            <button className="mt-4 w-full py-3 rounded-full bg-gradient-to-r from-[#FF7A00] to-[#FF2A5F] text-black font-outfit font-black active:scale-95" data-testid="artist-book-btn"
              onClick={() => window.open(`mailto:admin@hifireels.com?subject=Booking%20request%20-%20${encodeURIComponent(selected.first_name + ' ' + selected.last_name)}`)}>
              Book Now
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
